import { useEffect, useRef, useState } from 'react';
import { convertS3PathToHttpUrl } from '@/utils/s3Utils';

interface ProcessingStatus {
  isProcessed: boolean;
  isLoading: boolean;
  hasFailed: boolean;
  error?: string;
}

const DEFAULT_MAX_POLL_DURATION_MS = 30 * 60 * 1000;

/**
 * Hook to check if a video segment is processed by polling the pathKey URL.
 *
 * @reason Stops the spinner under three conditions: (a) the file appears in S3
 * (200/206), (b) the parent has observed a failed status from the segment API
 * (passed via `hasFailed`), or (c) the elapsed polling time exceeds
 * `maxPollDurationMs`. File existence alone is unsafe — when an ECS task is
 * never actually launched (e.g. Fargate vCPU quota rejection), the file never
 * appears, so the previous version polled forever.
 *
 * @param pathKey - S3 path to the processed video file
 * @param enabled - Whether to enable the status checking
 * @param options - Optional behaviour overrides
 * @param options.pollingIntervalMs - Polling interval (default 3000ms)
 * @param options.maxPollDurationMs - Give up after this many ms (default 30min)
 * @param options.hasFailed - External failure signal (e.g. segment.status === 'Failed')
 */
export const useProcessingStatus = (
  pathKey: string | undefined,
  enabled = true,
  options: {
    pollingIntervalMs?: number;
    maxPollDurationMs?: number;
    hasFailed?: boolean;
  } = {},
): ProcessingStatus => {
  const {
    pollingIntervalMs = 3000,
    maxPollDurationMs = DEFAULT_MAX_POLL_DURATION_MS,
    hasFailed: externalHasFailed = false,
  } = options;

  const [status, setStatus] = useState<ProcessingStatus>({
    isProcessed: false,
    isLoading: false,
    hasFailed: false,
  });

  const isProcessedRef = useRef(false);

  useEffect(() => {
    if (!pathKey || !enabled) {
      setStatus({ isProcessed: false, isLoading: false, hasFailed: false });
      isProcessedRef.current = false;
      return;
    }

    if (externalHasFailed) {
      setStatus({
        isProcessed: false,
        isLoading: false,
        hasFailed: true,
        error: 'Processing failed (reported by server)',
      });
      isProcessedRef.current = false;
      return;
    }

    let isMounted = true;
    let intervalId: NodeJS.Timeout | null = null;
    const startedAtMs = Date.now();

    const stopPolling = (): void => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const checkStatus = async (): Promise<void> => {
      if (!isMounted) return;

      if (Date.now() - startedAtMs > maxPollDurationMs) {
        setStatus({
          isProcessed: false,
          isLoading: false,
          hasFailed: true,
          error: `Processing timed out after ${Math.round(maxPollDurationMs / 60000)} minutes`,
        });
        isProcessedRef.current = false;
        stopPolling();
        return;
      }

      setStatus((prev) => ({ ...prev, isLoading: true, error: undefined }));

      try {
        // Accept either a raw S3 path (s3://...) or a pre-built HTTPS URL
        // (e.g. a signed `fullPath` from the API). The latter lets callers
        // bypass the deprecated public-web-api Lambda's broken pathKey logic
        // — see the comment at the call site for context.
        const httpUrl = pathKey.startsWith('s3://') ? convertS3PathToHttpUrl(pathKey) : pathKey;
        const response = await fetch(httpUrl, {
          method: 'GET',
          headers: {
            Range: 'bytes=0-0',
          },
        });

        // could be unmounted while waiting for fetch
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!isMounted) return;

        if (response.status === 200 || response.status === 206) {
          // File exists (200 for full response, 206 for partial content) - processing is complete
          setStatus({ isProcessed: true, isLoading: false, hasFailed: false });
          isProcessedRef.current = true;
          stopPolling();
        } else if (response.status === 404) {
          // File doesn't exist - still processing
          setStatus({ isProcessed: false, isLoading: false, hasFailed: false });
          isProcessedRef.current = false;
        } else {
          // Other status codes
          setStatus({
            isProcessed: false,
            isLoading: false,
            hasFailed: false,
            error: `Unexpected response: ${response.status}`,
          });
          isProcessedRef.current = false;
        }
      } catch (error) {
        // could be unmounted while waiting for fetch
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!isMounted) return;

        setStatus({
          isProcessed: false,
          isLoading: false,
          hasFailed: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
        isProcessedRef.current = false;
      }
    };

    // Initial check
    void checkStatus();

    // Set up polling interval to continue checking until processed
    intervalId = setInterval(() => {
      // Only continue polling if component is mounted and not yet processed
      if (isMounted && !isProcessedRef.current) {
        void checkStatus();
      }
    }, pollingIntervalMs);

    return () => {
      isMounted = false;
      stopPolling();
    };
  }, [pathKey, enabled, pollingIntervalMs, maxPollDurationMs, externalHasFailed]);

  return status;
};
